const alignContent = {
    name: "align-content",
    path: "/align-content",
    title: "align-content",
    description: "Utilities for controlling how rows are aligned in multi-row grid containers.",
    value: {
        "align-content": {
            "content-start": "flex-start",
            "content-end": "flex-end",
            "content-center": "center",
            "content-between": "space-between",
            "content-around": "space-around",
            "content-evenly": "space-evenly",
            "content-stretch": "stretch",
            "content-normal": "normal",
            "content-baseline": "baseline",
        },
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="grid grid-rows-4 h-64 gap-2 content-start">
  <div class="bg-color-pink-500 text-white p-2">01</div>
  <div class="bg-color-pink-500 text-white p-2">02</div>
  <div class="bg-color-pink-500 text-white p-2">03</div>
  <div class="bg-color-pink-500 text-white p-2">04</div>
</div>

<div class="grid grid-rows-4 h-64 gap-2 content-end mt-6">
  <div class="bg-color-pink-500 text-white p-2">01</div>
  <div class="bg-color-pink-500 text-white p-2">02</div>
  <div class="bg-color-pink-500 text-white p-2">03</div>
  <div class="bg-color-pink-500 text-white p-2">04</div>
</div>

<div class="grid grid-rows-4 h-64 gap-2 content-center mt-6">
  <div class="bg-color-pink-500 text-white p-2">01</div>
  <div class="bg-color-pink-500 text-white p-2">02</div>
  <div class="bg-color-pink-500 text-white p-2">03</div>
  <div class="bg-color-pink-500 text-white p-2">04</div>
</div>

<div class="grid grid-rows-4 h-64 gap-2 content-between mt-6">
  <div class="bg-color-pink-500 text-white p-2">01</div>
  <div class="bg-color-pink-500 text-white p-2">02</div>
  <div class="bg-color-pink-500 text-white p-2">03</div>
  <div class="bg-color-pink-500 text-white p-2">04</div>
</div>

<div class="grid grid-rows-4 h-64 gap-2 content-around mt-6">
  <div class="bg-color-pink-500 text-white p-2">01</div>
  <div class="bg-color-pink-500 text-white p-2">02</div>
  <div class="bg-color-pink-500 text-white p-2">03</div>
  <div class="bg-color-pink-500 text-white p-2">04</div>
</div>

<div class="grid grid-rows-4 h-64 gap-2 content-evenly mt-6">
  <div class="bg-color-pink-500 text-white p-2">01</div>
  <div class="bg-color-pink-500 text-white p-2">02</div>
  <div class="bg-color-pink-500 text-white p-2">03</div>
  <div class="bg-color-pink-500 text-white p-2">04</div>
</div>

<div class="grid grid-rows-4 h-64 gap-2 content-stretch mt-6">
  <div class="bg-color-pink-500 text-white p-2">01</div>
  <div class="bg-color-pink-500 text-white p-2">02</div>
  <div class="bg-color-pink-500 text-white p-2">03</div>
  <div class="bg-color-pink-500 text-white p-2">04</div>
</div>
                `
            },
        },
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">content-*</code> utilities to control how rows are aligned in a multi-row grid container.
            </p>
            <div class="grid md:grid-cols-2 gap-10">
                <div>
                    <small class="text-center block mb-4">content-start</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-2 content-start font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-end</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-2 content-end font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-center</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-2 content-center font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-between</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-2 content-between font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-around</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-2 content-around font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-evenly</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-2 content-evenly font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-stretch</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-2 content-stretch font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center rounded-lg">04</div>
                        </div>
                    </div>
                </div>
            </div>
            `
        },
    ]
};

export default alignContent;

<div className="sr-only">

    <div class="grid md:grid-cols-2 gap-10 h-56">
                <div>
                    <small class="text-center block mb-4">content-start</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-2 content-start font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-end</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-rows-4 h-full gap-2 content-end font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-center</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-rows-4 h-full gap-2 content-center font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-between</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-rows-4 h-full gap-2 content-between font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-around</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-rows-4 h-full gap-2 content-around font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-evenly</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-rows-4 h-full gap-2 content-evenly font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">04</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">content-stretch</small>
                    <div class="relative h-64">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-rows-4 h-full gap-2 content-stretch font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">03</div>
                            <div class="bg-color-pink-500 p-2 rounded-lg">04</div>
                        </div>
                    </div>
                </div>
            </div>
</div>